import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from './irlObservations2026.module.css';
import { AI_REVIEW_MODES } from './dummyData';
import {
    acceptIrlProposal,
    bulkDeleteIrlRequests,
    bulkUpdateIrlFields,
    createIrlFollowUp,
    createIrlRequest,
    deleteIrlComment,
    dismissIrlProposal,
    editIrlComment,
    getIrlAiReview,
    getIrlFolders,
    getIrlMentionCandidates,
    getIrlScreen,
    hasIrlProjectId,
    markIrlDiscussionRead,
    openIrlEvidence,
    postIrlComment,
    reopenIrlComment,
    resolveIrlComment,
    saveIrlDraft,
    startIrlAiReview,
    submitIrlResponse,
    transitionIrl,
    updateIrlFields,
    uploadIrlEvidence,
} from '../../apiServices/irlScreenApi';
import {
    applyBulkDraftPatch,
    downloadCsv,
    filledUpdates,
    formatBulkDeleteResult,
    IRL_EXPORT_COLUMNS,
    listExportFilenames,
    pruneSelection,
    sharedRowValue,
    toIsoDueDate,
} from './listActions';
import { exportListPdf } from './listPdf';
import { GetIRLDocData } from '../../redux/documentsSlice';
import FileViewerModal from '../Dataroom2.0/FileViewerModal';
import { IrlModuleHeader, IrlNavTree, ModuleShell, isOpenIrlLayout, isRequestOverdue, requestStatusLabel } from './chrome';
import IrlMain from './screens/IRL_Main';
import IrlStockQuestions from './screens/IRL_Stock_Questions';
import IrlGenerateInitial from './screens/IRL_Generate_Initial';
import IrlOverview from './screens/IRL_Overview';
import IrlOpen from './screens/IRL_Open';
import IrlResponse from './screens/IRL_Response';
import IrlNoResponse from './screens/IRL_No_Response';
import IrlEvidence from './screens/IRL_Evidence';
import IrlRelated from './screens/IRL_Related';
import IrlHistory from './screens/IRL_History';
import IrlDiscussion from './screens/IRL_Discussion';
import { AiReviewReadyBanner, evidenceDocumentId, evidenceIncludesUploads, mentionIdsFromText, stripHtmlText, withPlainRequestText } from './screens/shared';
import IrlCreateRequest from './screens/IRL_Create_Request';
import IrlAiReview from './screens/IRL_AI_Review';
import IrlAiReviewScope from './screens/IRL_AI_Review_Review_Response_1';
import IrlAiReviewInstructions from './screens/IRL_AI_Review_Review_Response_2';
import IrlAiReviewConfirm from './screens/IRL_AI_Review_Review_Response_3';
import IrlAiReviewProgress from './screens/IRL_AI_Review_Review_Response_4';
import IrlAiReviewComplete from './screens/IRL_AI_Review_Review_Response_5';
import IrlAiSuggestionsResponses from './screens/IRL_AI_Review_Review_Suggestions_Responses';
import IrlAiSuggestionsObservations from './screens/IRL_AI_Review_Review_Suggestions_Observations';
import IrlAiSuggestionsFollowup from './screens/IRL_AI_Review_Review_Suggestions_Followup';
import IrlAiSuggestionsContradictions from './screens/IRL_AI_Review_Review_Suggestions_Contradictions';

const DETAIL_TABS = ['Overview', 'Response', 'Evidence', 'Related', 'History', 'Discussion'];
const SUGGEST_TABS = ['Responses', 'Observations', 'Follow-up IRLs', 'Contradictions'];
const LIVE_AI_MODES = AI_REVIEW_MODES.filter(mode => ['initial', 'responses', 'answers', 'gaps', 'full'].includes(mode.id));
const CANNOT_PROVIDE_REASONS = [
    { id: 'DOES_NOT_EXIST', label: 'Does not exist' },
    { id: 'UNAVAILABLE', label: 'Unavailable' },
    { id: 'CONFIDENTIAL', label: 'Confidential / restricted' },
    { id: 'WILL_PROVIDE_LATER', label: 'Will provide later' },
    { id: 'OTHER', label: 'Other' },
];

function looksLikeHtml(value) {
    if (typeof value !== 'string') return false;
    const text = value.trimStart().slice(0, 256).toLowerCase();
    return text.startsWith('<!doctype') || text.startsWith('<html') || text.includes('<body') || text.includes('<pre');
}

function errorText(error) {
    const data = error?.response?.data;
    if (!data) return 'The request could not be completed.';
    if (typeof data === 'string') {
        if (looksLikeHtml(data)) return 'The request could not be completed.';
        return data;
    }
    if (typeof data.detail === 'string') {
        if (looksLikeHtml(data.detail)) return 'The request could not be completed.';
        return data.detail;
    }
    const first = Object.values(data)[0];
    if (Array.isArray(first)) return String(first[0]);
    if (first && typeof first === 'object') return String(Object.values(first)[0]);
    if (typeof first === 'string') {
        if (looksLikeHtml(first)) return 'The request could not be completed.';
        return first;
    }
    return 'The request could not be completed.';
}

function filterRequests(requests, { status, workstream, priority, query }) {
    return requests.filter(item => {
        const label = requestStatusLabel(item);
        const overdue = isRequestOverdue(item);
        if (status && status !== 'All') {
            if (status === 'Overdue') {
                if (!overdue) return false;
            } else if (label !== status) {
                return false;
            }
        }
        if (workstream !== 'all' && String(item.workstreamId) !== String(workstream)) return false;
        if (priority !== 'all' && item.priority !== priority) return false;
        if (query) {
            const haystack = `${item.id} ${stripHtmlText(item.title)}`.toLowerCase();
            if (!haystack.includes(query.toLowerCase())) return false;
        }
        return true;
    });
}

function plainRequests(rows) {
    return (rows || []).map(withPlainRequestText);
}

function emptyDraft(workstream = '') {
    return {
        question: '',
        context: '',
        workstream,
        priority: 'High',
        owner: '',
        due: '',
        folder: '',
        related: '',
        relatedQuery: '',
    };
}

function emptyIrlBulk() {
    return { owner: '', workstream: '', folder: '', due: '', priority: '' };
}

function irlExportRows(rows) {
    return (rows || []).map(item => ({
        id: item.id,
        title: stripHtmlText(item.title),
        context: stripHtmlText(item.context || ''),
        workstream: item.workstream,
        status: requestStatusLabel(item),
        priority: item.priority,
        due: item.dueDate || toIsoDueDate(item.due) || '',
        owner: item.owner,
        folder: item.folder || '',
    }));
}

function reviewRunId(review) {
    return review?.id ?? review?.pk ?? review?.runId ?? review?.run_id ?? null;
}

function proposalKey(item) {
    return item?.proposalId ?? item?.proposal_id ?? null;
}

function requestDisplayId(item) {
    if (item == null) return '';
    if (typeof item === 'string' || typeof item === 'number') return String(item);
    return item.id || item.irlId || item.irl_id || item.parentId || '';
}

function normalizeSuggestionCards(cards, runId) {
    return (cards || []).map(card => {
        const id = card.id || card.irlId || card.irl_id || card.parentId || '';
        return {
            ...card,
            id,
            irlId: card.irlId || card.irl_id || id,
            proposalId: card.proposalId ?? card.proposal_id ?? null,
            reviewId: card.reviewId ?? card.review_id ?? runId ?? null,
            proposalKind: card.proposalKind || card.proposal_kind || 'RESPONSE',
            status: card.status || 'PENDING',
        };
    });
}

function isPendingSuggestion(item) {
    return !item.status || item.status === 'PENDING';
}

const ACTIVE_REVIEW_STATUSES = ['QUEUED', 'RUNNING'];
const FINISHED_REVIEW_STATUSES = ['COMPLETED', 'COMPLETED_WITH_LIMITATIONS'];
const EVIDENCE_UPLOAD_POLL_MS = 2000;
const EVIDENCE_UPLOAD_WAIT_MS = 120000;
const EVIDENCE_STILL_PROCESSING = 'The file is still processing. You can stay on this tab.';

function sleep(ms) {
    return new Promise(resolve => window.setTimeout(resolve, ms));
}

function isActiveReviewStatus(status) {
    return ACTIVE_REVIEW_STATUSES.includes(status);
}

function isFinishedReviewStatus(status) {
    return FINISHED_REVIEW_STATUSES.includes(status);
}

function suggestTabForMode(mode) {
    if (mode === 'FIND_GAPS' || mode === 'gaps') return 'Follow-up IRLs';
    return 'Responses';
}

export default function IrlApp({ informationRequestModal, setInformationRequestModal }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { selectedProject } = useSelector(state => state.projectData);
    const [requests, setRequests] = React.useState([]);
    const [workstreams, setWorkstreams] = React.useState([]);
    const [members, setMembers] = React.useState([]);
    const [folders, setFolders] = React.useState([]);
    const [loadError, setLoadError] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [uploadingEvidence, setUploadingEvidence] = React.useState(false);
    const evidenceUploadWaitRef = React.useRef(0);
    const [responseDraft, setResponseDraft] = React.useState('');
    const [suggestions, setSuggestions] = React.useState([]);
    const [reviewResult, setReviewResult] = React.useState(null);
    const [reviewMeta, setReviewMeta] = React.useState(null);
    const [reviewId, setReviewId] = React.useState(null);
    const [runStatus, setRunStatus] = React.useState(null);
    const [status, setStatus] = React.useState('All');
    const [workstream, setWorkstream] = React.useState('all');
    const [priority, setPriority] = React.useState('all');
    const [query, setQuery] = React.useState('');
    const [page, setPage] = React.useState('list');
    const [selectedId, setSelectedId] = React.useState(null);
    const [detailTab, setDetailTab] = React.useState('Overview');
    const [suggestTab, setSuggestTab] = React.useState('Responses');
    const [createOpen, setCreateOpen] = React.useState(false);
    const [menuOpen, setMenuOpen] = React.useState(false);
    const [selectedIds, setSelectedIds] = React.useState([]);
    const [bulkDraft, setBulkDraft] = React.useState(emptyIrlBulk());
    const [bulkFolders, setBulkFolders] = React.useState([]);
    const [listNotice, setListNotice] = React.useState('');
    const [aiStep, setAiStep] = React.useState(null);
    const [aiMode, setAiMode] = React.useState('responses');
    const [aiScope, setAiScope] = React.useState('Entire IRL');
    const [aiInstructions, setAiInstructions] = React.useState('');
    const [draft, setDraft] = React.useState(emptyDraft());
    const [comment, setComment] = React.useState('');
    const [mentionCandidates, setMentionCandidates] = React.useState([]);
    const [replyFor, setReplyFor] = React.useState(null);
    const [replyText, setReplyText] = React.useState('');
    const [copied, setCopied] = React.useState(false);
    const [editingResponse, setEditingResponse] = React.useState(false);
    const [copiedCommentId, setCopiedCommentId] = React.useState(null);
    const [focusCommentId, setFocusCommentId] = React.useState(null);
    const [cannotProvideOpen, setCannotProvideOpen] = React.useState(false);
    const [viewerOpen, setViewerOpen] = React.useState(false);
    const [viewerFile, setViewerFile] = React.useState(null);
    const [viewerWorkstream, setViewerWorkstream] = React.useState(null);

    const hasProject = hasIrlProjectId();
    const selected = requests.find(item => item.id === selectedId);
    const filtered = filterRequests(requests, { status, workstream, priority, query });
    const selectedRows = requests.filter(item => selectedIds.includes(item.id));
    const folderWorkstream = bulkDraft.workstream || sharedRowValue(selectedRows, 'workstreamId');
    const exportNames = listExportFilenames('Information Requests', selectedProject);
    const hasPendingSuggestions =
        Boolean(reviewId) &&
        isFinishedReviewStatus(runStatus) &&
        suggestions.some(isPendingSuggestion);
    const showReadyBanner = hasProject && hasPendingSuggestions && !aiStep;
    const headerStats = {
        open: requests.filter(item => {
            const label = requestStatusLabel(item);
            return label === 'Open' || label === 'In progress';
        }).length,
        needsReview: requests.filter(item => requestStatusLabel(item) === 'Needs review').length,
        overdue: requests.filter(item => isRequestOverdue(item)).length,
    };
    const aiScopes = React.useMemo(() => {
        const names = (workstreams || []).map(item => item.name);
        return ['Entire IRL', ...names, 'Open requests only', 'This IRL only'];
    }, [workstreams]);

    const applyReviewPayload = React.useCallback(review => {
        if (!review) return;
        const runId = reviewRunId(review);
        setReviewId(runId);
        setReviewResult(review.result || null);
        setReviewMeta({
            createdAt: review.createdAt,
            scopeLabel: review.scopeLabel || review.scope?.label || '',
            scope: review.scope,
            limitations: review.limitations,
            newEvidenceBanner: review.newEvidenceBanner || review.result?.newEvidenceBanner || '',
        });
        setSuggestions(normalizeSuggestionCards(review.suggestions, runId));
        setRunStatus(review.status || null);
        if (review.mode === 'FIND_GAPS' || review.mode === 'gaps') {
            setAiMode('gaps');
        }
    }, []);

    const openWaitingSuggestions = React.useCallback((review, options = {}) => {
        setAiStep(null);
        setPage('suggestions');
        if (options.keepTab !== true) {
            setSuggestTab(suggestTabForMode(review?.mode));
        }
    }, []);

    const reopenPendingSuggestions = React.useCallback(() => {
        openWaitingSuggestions(null, { keepTab: true });
    }, [openWaitingSuggestions]);

    const reload = React.useCallback(async () => {
        if (!hasIrlProjectId()) return [];
        const data = await getIrlScreen();
        const rows = plainRequests(data.requests);
        setRequests(rows);
        setWorkstreams(data.workstreams || []);
        setMembers(data.members || []);
        setLoadError('');
        return rows;
    }, []);

    React.useEffect(() => {
        if (!hasIrlProjectId()) {
            setRequests([]);
            setWorkstreams([]);
            setMembers([]);
            setLoadError('');
            return undefined;
        }
        let cancelled = false;
        const requestSelected = Boolean(searchParams.get('request'));
        getIrlScreen()
            .then(data => {
                if (cancelled) return;
                setRequests(plainRequests(data.requests));
                setWorkstreams(data.workstreams || []);
                setMembers(data.members || []);
                if (data.workstreams?.[0]) {
                    setDraft(current => ({ ...current, workstream: current.workstream || String(data.workstreams[0].id) }));
                }
                const running = data.aiReviewInProgress;
                const waiting = data.aiReview;
                if (running) {
                    applyReviewPayload(running);
                    setAiStep('progress');
                    return;
                }
                if (waiting) {
                    applyReviewPayload(waiting);
                    if (!requestSelected) {
                        openWaitingSuggestions(waiting);
                    }
                }
            })
            .catch(error => {
                if (!cancelled) setLoadError(errorText(error));
            });
        return () => {
            cancelled = true;
        };
    }, [applyReviewPayload, openWaitingSuggestions, searchParams]);

    React.useEffect(() => {
        if (!hasIrlProjectId() || !reviewId || !isActiveReviewStatus(runStatus)) return undefined;
        let cancelled = false;
        const poll = async () => {
            try {
                const review = await getIrlAiReview(reviewId);
                if (cancelled) return;
                if (isActiveReviewStatus(review.status)) {
                    setRunStatus(review.status);
                    return;
                }
                applyReviewPayload(review);
                if (review.status === 'FAILED') {
                    setLoadError((review.result && review.result.error) || 'The AI Review could not be completed.');
                    setAiStep(null);
                    return;
                }
                if (isFinishedReviewStatus(review.status)) {
                    setAiStep('complete');
                }
            } catch (error) {
                if (!cancelled) setLoadError(errorText(error));
            }
        };
        poll();
        const timer = window.setInterval(poll, 2500);
        return () => {
            cancelled = true;
            window.clearInterval(timer);
        };
    }, [applyReviewPayload, reviewId, runStatus]);

    React.useEffect(() => {
        if (!hasIrlProjectId() || !draft.workstream) {
            setFolders([]);
            return undefined;
        }
        let cancelled = false;
        getIrlFolders(draft.workstream)
            .then(data => {
                if (!cancelled) setFolders(data.folders || []);
            })
            .catch(() => {
                if (!cancelled) setFolders([]);
            });
        return () => {
            cancelled = true;
        };
    }, [draft.workstream]);

    React.useEffect(() => {
        setSelectedIds(current => pruneSelection(current, requests.map(item => item.id)));
    }, [requests]);

    React.useEffect(() => {
        if (!hasIrlProjectId() || !folderWorkstream) {
            setBulkFolders([]);
            return undefined;
        }
        let cancelled = false;
        getIrlFolders(folderWorkstream)
            .then(data => {
                if (!cancelled) setBulkFolders(data.folders || []);
            })
            .catch(() => {
                if (!cancelled) setBulkFolders([]);
            });
        return () => {
            cancelled = true;
        };
    }, [folderWorkstream]);

    const openDetail = React.useCallback((id, tab = 'Overview') => {
        setSelectedId(id);
        setDetailTab(tab);
        setPage('detail');
    }, []);

    React.useEffect(() => {
        const id = searchParams.get('request');
        const commentId = searchParams.get('comment');
        if (id) openDetail(id, commentId ? 'Discussion' : 'Overview');
        if (commentId) setFocusCommentId(Number(commentId) || commentId);
    }, [searchParams, openDetail]);

    React.useEffect(() => {
        if (informationRequestModal && hasIrlProjectId()) setCreateOpen(true);
    }, [informationRequestModal]);

    React.useEffect(() => {
        setResponseDraft(selected?.draftResponse || '');
        setEditingResponse(false);
    }, [selected?.id]);

    React.useEffect(() => {
        if (!hasIrlProjectId() || page !== 'detail' || detailTab !== 'Discussion' || !selected?.pk) return undefined;
        let cancelled = false;
        markIrlDiscussionRead(selected.pk)
            .then(updated => {
                if (cancelled || !updated) return;
                setRequests(current => current.map(item => (item.pk === updated.pk ? withPlainRequestText({ ...item, ...updated, newComments: 0 }) : item)));
            })
            .catch(() => {});
        getIrlMentionCandidates(selected.pk)
            .then(data => {
                if (!cancelled) setMentionCandidates(data.members || []);
            })
            .catch(() => {
                if (!cancelled) setMentionCandidates([]);
            });
        return () => {
            cancelled = true;
        };
    }, [page, detailTab, selected?.pk]);

    const handleCreate = async () => {
        if (!hasIrlProjectId() || !draft.question.trim() || busy) return;
        setBusy(true);
        try {
            const created = await createIrlRequest({
                question: draft.question,
                description: draft.context,
                workstream_id: draft.workstream,
                priority: draft.priority,
                due_date: draft.due,
                related_irl: draft.related,
                destination_folder: draft.folder || undefined,
                owner: draft.owner || undefined,
            });
            await reload();
            setCreateOpen(false);
            setInformationRequestModal?.(false);
            setDraft(emptyDraft(draft.workstream));
            openDetail(created.id);
        } catch (error) {
            setLoadError(errorText(error));
        } finally {
            setBusy(false);
        }
    };

    const handleSubmitResponse = async () => {
        if (!hasIrlProjectId() || !selected?.pk || !responseDraft.trim() || busy) return;
        setBusy(true);
        try {
            const updated = await submitIrlResponse(selected.pk, responseDraft);
            setResponseDraft('');
            setEditingResponse(false);
            await reload();
            openDetail(updated.id, 'Response');
        } catch (error) {
            setLoadError(errorText(error));
        } finally {
            setBusy(false);
        }
    };

    const handleUpdateFields = async body => {
        if (!hasIrlProjectId() || !selected?.pk || busy) return false;
        setBusy(true);
        try {
            const updated = await updateIrlFields(selected.pk, body);
            setRequests(current => current.map(item => (item.pk === updated.pk ? withPlainRequestText(updated) : item)));
            return true;
        } catch (error) {
            setLoadError(errorText(error));
            return false;
        } finally {
            setBusy(false);
        }
    };

    const handleExportCsv = () => {
        if (!filtered.length) {
            setListNotice('Nothing to export.');
            return;
        }
        downloadCsv(irlExportRows(filtered), IRL_EXPORT_COLUMNS, exportNames.csv);
        setListNotice(`Exported ${filtered.length} request${filtered.length === 1 ? '' : 's'} to CSV.`);
    };

    const handleExportPdf = async () => {
        if (!filtered.length) {
            setListNotice('Nothing to export.');
            return;
        }
        try {
            await exportListPdf({
                rows: irlExportRows(filtered),
                columns: IRL_EXPORT_COLUMNS,
                title: 'Information Requests',
                projectName: selectedProject?.name || selectedProject?.project_name,
                filename: exportNames.pdf,
            });
            setListNotice(`Exported ${filtered.length} request${filtered.length === 1 ? '' : 's'} to PDF.`);
        } catch (error) {
            setListNotice(error?.message || 'The PDF could not be exported.');
        }
    };

    const handleBulkApply = async () => {
        const values = filledUpdates(bulkDraft);
        if (!hasIrlProjectId() || !selectedRows.length || !Object.keys(values).length || busy) return;
        const updates = {};
        if (values.owner) updates.owner = values.owner;
        if (values.workstream) updates.workstream_id = values.workstream;
        if (values.folder) updates.destination_folder = values.folder;
        if (values.due) updates.due_date = values.due;
        if (values.priority) updates.priority = values.priority;
        setBusy(true);
        try {
            const result = await bulkUpdateIrlFields({ ids: selectedRows.map(item => item.pk), updates });
            const updated = result?.updated || [];
            if (updated.length) {
                setRequests(current => {
                    const byPk = new Map(updated.map(item => [item.pk, withPlainRequestText(item)]));
                    return current.map(item => byPk.get(item.pk) || item);
                });
            } else {
                await reload();
            }
            setBulkDraft(emptyIrlBulk());
            setListNotice(`Updated ${selectedRows.length} request${selectedRows.length === 1 ? '' : 's'}.`);
        } catch (error) {
            setLoadError(errorText(error));
        } finally {
            setBusy(false);
        }
    };

    const handleBulkDraft = patch => {
        setBulkDraft(current => applyBulkDraftPatch(current, patch));
    };

    const handleBulkDelete = async () => {
        if (!hasIrlProjectId() || !selectedRows.length || busy) return;
        setBusy(true);
        try {
            const result = await bulkDeleteIrlRequests({ ids: selectedRows.map(item => item.pk) });
            const deleted = result?.deleted || [];
            const failed = result?.failed || [];
            setSelectedIds([]);
            setBulkDraft(emptyIrlBulk());
            await reload();
            setListNotice(
                formatBulkDeleteResult(
                    { deleted: deleted.length || result?.count || 0, failed },
                    'information request',
                    'information requests',
                ),
            );
        } catch (error) {
            setListNotice(errorText(error));
        } finally {
            setBusy(false);
        }
    };

    const handleSaveDraft = async () => {
        if (!hasIrlProjectId() || !selected?.pk || !responseDraft.trim() || busy) return;
        setBusy(true);
        try {
            const updated = await saveIrlDraft(selected.pk, responseDraft);
            setRequests(current => current.map(item => (item.pk === updated.pk ? withPlainRequestText(updated) : item)));
        } catch (error) {
            setLoadError(errorText(error));
        } finally {
            setBusy(false);
        }
    };

    const handleTransition = async (action, extra = {}) => {
        if (!hasIrlProjectId() || !selected?.pk || busy) return;
        let note = extra.note || '';
        let reason = extra.reason || '';
        if (action === 'not-applicable') {
            note = window.prompt('Why is this request not applicable?') || '';
            if (!note) return;
        }
        if (action === 'cannot-provide' && !reason) {
            setCannotProvideOpen(true);
            return;
        }
        setBusy(true);
        try {
            await transitionIrl(selected.pk, action, { note: note || '', reason });
            await reload();
            setCannotProvideOpen(false);
        } catch (error) {
            setLoadError(errorText(error));
        } finally {
            setBusy(false);
        }
    };

    const handleCopyLink = () => {
        const url = `${window.location.origin}/irl?request=${selectedId}`;
        if (navigator.clipboard?.writeText) navigator.clipboard.writeText(url);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
    };

    const handleEditResponse = () => {
        if (!selected?.response) return;
        if (!editingResponse) {
            setResponseDraft(selected.draftResponse || selected.response.body || '');
        }
        setEditingResponse(true);
    };

    const handleCancelEdit = () => {
        setEditingResponse(false);
        setResponseDraft(selected?.draftResponse || '');
    };

    const postComment = async (parentId) => {
        const body = parentId ? replyText : comment;
        if (!hasIrlProjectId() || !body.trim() || !selected?.pk || busy) return;
        const mentionIds = mentionIdsFromText(body, mentionCandidates);
        setBusy(true);
        try {
            await postIrlComment(selected.pk, body, parentId || undefined, mentionIds);
            setComment('');
            setReplyText('');
            setReplyFor(null);
            await reload();
        } catch (error) {
            setLoadError(errorText(error));
        } finally {
            setBusy(false);
        }
    };

    const handleEditComment = async (item, next, mentionIds) => {
        const text = String(next || '').trim();
        if (!text || !hasIrlProjectId() || !selected?.pk) return false;
        setBusy(true);
        try {
            await editIrlComment(selected.pk, item.id, next, mentionIds || mentionIdsFromText(next, mentionCandidates));
            await reload();
            return true;
        } catch (error) {
            setLoadError(errorText(error));
            return false;
        } finally {
            setBusy(false);
        }
    };

    const handleDeleteComment = async item => {
        if (!item?.id || !hasIrlProjectId() || !selected?.pk || busy) return false;
        setBusy(true);
        try {
            await deleteIrlComment(selected.pk, item.id);
            await reload();
            return true;
        } catch (error) {
            setLoadError(errorText(error));
            return false;
        } finally {
            setBusy(false);
        }
    };

    const handleCopyCommentLink = item => {
        const url = `${window.location.origin}/irl?request=${selectedId}&comment=${item.id}`;
        if (navigator.clipboard?.writeText) navigator.clipboard.writeText(url);
        setCopiedCommentId(item.id);
        window.setTimeout(() => setCopiedCommentId(null), 1500);
    };

    const handleResolveThread = async item => {
        if (!hasIrlProjectId() || !selected?.pk || busy) return;
        setBusy(true);
        try {
            await resolveIrlComment(selected.pk, item.id);
            await reload();
        } catch (error) {
            setLoadError(errorText(error));
        } finally {
            setBusy(false);
        }
    };

    const handleReopenThread = async item => {
        if (!hasIrlProjectId() || !selected?.pk || busy) return;
        setBusy(true);
        try {
            await reopenIrlComment(selected.pk, item.id);
            await reload();
        } catch (error) {
            setLoadError(errorText(error));
        } finally {
            setBusy(false);
        }
    };

    const handleUploadEvidence = async files => {
        if (!hasIrlProjectId() || !selected?.pk || !files?.length || busy || uploadingEvidence) return;
        const requestPk = selected.pk;
        const pendingFiles = Array.from(files);
        const pendingNames = pendingFiles.map(file => file.name);
        const beforeIds = new Set((selected.evidence || []).map(evidenceDocumentId).filter(Boolean));
        const beforeCount = (selected.evidence || []).length;
        const waitId = evidenceUploadWaitRef.current + 1;
        evidenceUploadWaitRef.current = waitId;
        const uploadVisible = request => evidenceIncludesUploads(request, { beforeIds, beforeCount, pendingNames });
        const applyRequest = updated => {
            if (!updated?.pk) return;
            setRequests(current => current.map(item => (item.pk === updated.pk ? withPlainRequestText(updated) : item)));
        };
        setLoadError('');
        setUploadingEvidence(true);
        setBusy(true);
        setPage('detail');
        setDetailTab('Evidence');
        try {
            const updated = await uploadIrlEvidence(requestPk, pendingFiles);
            if (waitId !== evidenceUploadWaitRef.current) return;
            applyRequest(updated);
            if (uploadVisible(updated)) return;
            const deadline = Date.now() + EVIDENCE_UPLOAD_WAIT_MS;
            while (Date.now() < deadline) {
                setBusy(false);
                await sleep(EVIDENCE_UPLOAD_POLL_MS);
                if (waitId !== evidenceUploadWaitRef.current) return;
                const data = await getIrlScreen();
                if (waitId !== evidenceUploadWaitRef.current) return;
                const rows = plainRequests(data.requests);
                setRequests(rows);
                const match = rows.find(item => item.pk === requestPk);
                if (uploadVisible(match)) return;
            }
            setLoadError(EVIDENCE_STILL_PROCESSING);
        } catch (error) {
            if (waitId === evidenceUploadWaitRef.current) setLoadError(errorText(error));
        } finally {
            if (waitId === evidenceUploadWaitRef.current) {
                setUploadingEvidence(false);
                setBusy(false);
            }
        }
    };

    const openViewer = (file, workstreamForFile) => {
        setViewerWorkstream(workstreamForFile || null);
        setViewerFile(file);
        setViewerOpen(true);
    };

    const handleOpenFile = async file => {
        const documentId = evidenceDocumentId(file);
        const workstreamId = file?.workstreamId ?? file?.workstream_id;
        try {
            setLoadError('');
            if (documentId && workstreamId) {
                const res = await dispatch(GetIRLDocData({ id: documentId, workstream: workstreamId }));
                if (res?.type !== 'IRLDOC/getIRLDocData/fulfilled') {
                    setLoadError('This file cannot be previewed.');
                    return;
                }
                const doc = res.payload?.data;
                if (!doc?.document && !doc?.document_link) {
                    setLoadError('This file cannot be previewed.');
                    return;
                }
                const ws =
                    workstreams.find(item => String(item.id) === String(workstreamId)) || {
                        id: workstreamId,
                    };
                openViewer(
                    {
                        ...doc,
                        filePath: file.path || [ws.name, doc.ws_folder].filter(Boolean).join('/'),
                    },
                    ws,
                );
                return;
            }
            if (!file?.openUrl) return;
            const data = await openIrlEvidence(file.openUrl);
            if (!data?.url) return;
            openViewer({
                id: documentId || undefined,
                document_name: file.name || data.filename,
                document: data.url,
                filePath: file.path || '',
            });
        } catch (error) {
            setLoadError(errorText(error));
        }
    };

    const openAiReview = () => {
        if (isActiveReviewStatus(runStatus)) {
            setLoadError('A review is already running.');
            setAiStep('progress');
            return;
        }
        setAiStep('choose');
    };

    const runAiReview = async () => {
        if (!hasIrlProjectId()) return;
        if (isActiveReviewStatus(runStatus)) {
            setLoadError('A review is already running.');
            setAiStep('progress');
            return;
        }
        const selectedScope = workstreams.find(item => item.name === aiScope);
        const scope = selectedScope ? { label: selectedScope.name, workstream_id: selectedScope.id } : { label: aiScope };
        setAiStep('progress');
        try {
            const review = await startIrlAiReview({
                mode: aiMode,
                scope,
                instructions: aiInstructions,
                request_id: aiScope === 'This IRL only' ? selectedId : undefined,
            });
            applyReviewPayload(review);
        } catch (error) {
            if (error?.response?.status === 409 && reviewRunId(error.response.data)) {
                applyReviewPayload(error.response.data);
                setLoadError('A review is already running.');
                setAiStep('progress');
                return;
            }
            setLoadError(errorText(error));
            setAiStep(null);
        }
    };

    const handleAccept = async (item, editedBody) => {
        const runId = item?.reviewId || reviewId;
        const id = proposalKey(item);
        if (!hasIrlProjectId() || !runId || !id || busy) return;
        setBusy(true);
        try {
            const result = await acceptIrlProposal(runId, id, editedBody);
            await reload();
            if (result?.suggestions) {
                setSuggestions(normalizeSuggestionCards(result.suggestions, reviewRunId(result) || runId));
                if (result.result) setReviewResult(result.result);
            } else {
                setSuggestions(current => current.map(card => (proposalKey(card) === id ? { ...card, status: 'ACCEPTED' } : card)));
            }
            if (item.proposalKind === 'FOLLOW_UP' && result?.id) openDetail(result.id);
        } catch (error) {
            setLoadError(errorText(error));
        } finally {
            setBusy(false);
        }
    };

    const handleDismiss = async item => {
        const runId = item?.reviewId || reviewId;
        const id = proposalKey(item);
        if (!hasIrlProjectId() || !runId || !id || busy) return;
        setBusy(true);
        try {
            const review = await dismissIrlProposal(runId, id);
            setSuggestions(normalizeSuggestionCards(review.suggestions, reviewRunId(review) || runId));
            if (review.result) setReviewResult(review.result);
        } catch (error) {
            setLoadError(errorText(error));
        } finally {
            setBusy(false);
        }
    };

    const handleFollowUp = async item => {
        const runId = item?.reviewId || reviewId;
        const id = proposalKey(item);
        if (!hasIrlProjectId() || !runId || !id || busy) return;
        setBusy(true);
        try {
            const created = await createIrlFollowUp(
                runId,
                id,
                item.unresolved || item.question || item.title || item.note || '',
            );
            await reload();
            setReviewResult(current => ({
                ...(current || {}),
                follow_ups: Number(current?.follow_ups || 0) + 1,
            }));
            if (created?.id) openDetail(created.id);
        } catch (error) {
            setLoadError(errorText(error));
        } finally {
            setBusy(false);
        }
    };

    const openSuggestion = itemOrId => {
        const raw = requestDisplayId(itemOrId);
        if (!raw) return;
        const suffix = String(raw).replace(/^IRL-/i, '');
        const match = requests.find(row => {
            if (row.id === raw || String(row.pk) === String(raw)) return true;
            return String(row.pk) === suffix || String(row.id).replace(/^IRL-/i, '') === suffix;
        });
        openDetail(match ? match.id : raw);
    };

    const extraActions = selected ? (
        <>
            <button type="button" className={styles.outlineBtn} onClick={() => handleTransition('needs-review')}>
                Needs review
            </button>
            {selected.workflowStatus === 'CLOSED' ? (
                <button type="button" className={styles.outlineBtn} onClick={() => handleTransition('reopen')}>
                    Reopen
                </button>
            ) : (
                <button type="button" className={styles.outlineBtn} onClick={() => handleTransition('close')}>
                    Close
                </button>
            )}
            <button type="button" className={styles.outlineBtn} onClick={() => handleTransition('cannot-provide')}>
                Cannot provide
            </button>
            <button type="button" className={styles.outlineBtn} onClick={() => handleTransition('not-applicable')}>
                Not applicable
            </button>
        </>
    ) : null;

    const detailProps = {
        request: selected,
        tab: detailTab,
        onTab: setDetailTab,
        onBack: () => setPage('list'),
        onOpenAi: openAiReview,
        onCopy: handleCopyLink,
        copied,
        onRelatedIrl: openDetail,
        onRelatedObs: id => navigate(`/observations?observation=${id}`),
        comment,
        onComment: setComment,
        onPostComment: () => postComment(),
        tabs: DETAIL_TABS,
        extraActions,
        onUpdateFields: handleUpdateFields,
        responseDraft,
        onResponseDraft: setResponseDraft,
        onSubmitResponse: handleSubmitResponse,
        onSaveDraft: handleSaveDraft,
        onEdit: handleEditResponse,
        onCancelEdit: handleCancelEdit,
        editingResponse,
        onUploadEvidence: handleUploadEvidence,
        uploadingEvidence,
        onOpenFile: handleOpenFile,
        mentionCandidates,
        onReply: id => setReplyFor(current => (id == null || current === id ? null : id)),
        replyFor,
        replyText,
        onReplyText: setReplyText,
        onSubmitReply: () => postComment(replyFor),
        onEditComment: handleEditComment,
        onDeleteComment: handleDeleteComment,
        onCopyComment: handleCopyCommentLink,
        copiedCommentId,
        onResolveThread: handleResolveThread,
        onReopenThread: handleReopenThread,
        focusCommentId,
        busy,
    };

    const suggestionProps = {
        tab: suggestTab,
        tabs: SUGGEST_TABS,
        onTab: setSuggestTab,
        onBack: () => setPage('list'),
        counts: reviewResult,
        reviewMeta,
    };

    return (
        <>
        <ModuleShell>
            <IrlModuleHeader stats={headerStats} />
            {hasProject && loadError && <p className={styles.contentSubtitle}>{loadError}</p>}
            {showReadyBanner && page === 'detail' && (
                <AiReviewReadyBanner onReview={reopenPendingSuggestions} />
            )}
            {hasProject && isActiveReviewStatus(runStatus) && aiStep !== 'progress' && (
                <div className={styles.alert}>
                    <p className={styles.alertTitle}>AI Review in progress</p>
                    <p className={styles.alertBody}>You can leave this page. We will email you when the review is ready.</p>
                    <button type="button" className={styles.alertLink} onClick={() => setAiStep('progress')}>
                        View progress
                    </button>
                </div>
            )}
            <div className={styles.body}>
                <IrlNavTree
                    status={status}
                    workstream={workstream}
                    requests={requests}
                    onStatus={value => {
                        setStatus(value);
                        setPage('list');
                    }}
                    onWorkstream={value => {
                        setWorkstream(value);
                        setPage('list');
                    }}
                />
                <div className={styles.main}>
                    <div className={styles.mainScroll}>
                        {!hasProject && (
                            <div className={styles.contentHeader}>
                                <div>
                                    <h2 className={styles.contentTitle}>Information Requests</h2>
                                    <p className={styles.contentSubtitle}>No project is selected.</p>
                                </div>
                            </div>
                        )}
                        {hasProject && page === 'list' && (
                            <IrlMain
                                requests={filtered}
                                status={status}
                                onStatus={setStatus}
                                workstream={workstream}
                                onWorkstream={setWorkstream}
                                priority={priority}
                                onPriority={setPriority}
                                query={query}
                                onQuery={setQuery}
                                onOpen={openDetail}
                                onCreate={() => setCreateOpen(true)}
                                onAiReview={openAiReview}
                                onReviewSuggestions={showReadyBanner ? reopenPendingSuggestions : undefined}
                                menuOpen={menuOpen}
                                onMenu={setMenuOpen}
                                workstreams={workstreams}
                                members={members}
                                folders={bulkFolders}
                                selectedIds={selectedIds}
                                onSelectedIds={setSelectedIds}
                                bulkDraft={bulkDraft}
                                onBulkDraft={handleBulkDraft}
                                onBulkApply={handleBulkApply}
                                onBulkDelete={handleBulkDelete}
                                onExportCsv={handleExportCsv}
                                onExportPdf={handleExportPdf}
                                onStockQuestions={() => {
                                    setMenuOpen(false);
                                    setPage('stock');
                                }}
                                notice={listNotice}
                                busy={busy}
                            />
                        )}
                        {hasProject && page === 'generate' && (
                            <IrlGenerateInitial
                                workstreams={workstreams}
                                existingRequests={requests}
                                onBack={() => setPage('list')}
                                onCreated={async message => {
                                    setPage('list');
                                    setListNotice(message);
                                    await reload();
                                }}
                            />
                        )}
                        {hasProject && page === 'stock' && (
                            <IrlStockQuestions
                                workstreams={workstreams}
                                defaultWorkstreamId={workstream}
                                busy={busy}
                                notice={listNotice}
                                onNotice={setListNotice}
                                onBusy={setBusy}
                                onBack={async () => {
                                    setPage('list');
                                    await reload();
                                }}
                                onImported={async () => {
                                    await reload();
                                    setPage('list');
                                }}
                            />
                        )}
                        {page === 'detail' && selected && detailTab === 'Overview' && (isOpenIrlLayout(selected) ? <IrlOpen {...detailProps} /> : <IrlOverview {...detailProps} />)}
                        {page === 'detail' && selected && detailTab === 'Response' && (selected.response ? <IrlResponse {...detailProps} /> : <IrlNoResponse {...detailProps} />)}
                        {page === 'detail' && selected && detailTab === 'Evidence' && <IrlEvidence {...detailProps} />}
                        {page === 'detail' && selected && detailTab === 'Related' && <IrlRelated {...detailProps} />}
                        {page === 'detail' && selected && detailTab === 'History' && <IrlHistory {...detailProps} />}
                        {page === 'detail' && selected && detailTab === 'Discussion' && <IrlDiscussion {...detailProps} />}
                        {page === 'suggestions' && suggestTab === 'Responses' && (
                            <IrlAiSuggestionsResponses
                                {...suggestionProps}
                                items={suggestions.filter(item => (item.proposalKind || 'RESPONSE') === 'RESPONSE' && isPendingSuggestion(item))}
                                onOpen={openSuggestion}
                                onAccept={handleAccept}
                                onDismiss={handleDismiss}
                                onFollowUp={handleFollowUp}
                                onOpenFile={handleOpenFile}
                                busy={busy}
                            />
                        )}
                        {page === 'suggestions' && suggestTab === 'Observations' && (
                            <IrlAiSuggestionsObservations
                                {...suggestionProps}
                                items={suggestions.filter(item => item.proposalKind === 'OBSERVATION' && isPendingSuggestion(item))}
                                onAccept={handleAccept}
                                onDismiss={handleDismiss}
                                busy={busy}
                            />
                        )}
                        {page === 'suggestions' && suggestTab === 'Follow-up IRLs' && (
                            <IrlAiSuggestionsFollowup
                                {...suggestionProps}
                                items={suggestions.filter(item => item.proposalKind === 'FOLLOW_UP' && isPendingSuggestion(item))}
                                onOpen={openSuggestion}
                                onAccept={handleAccept}
                                onDismiss={handleDismiss}
                                busy={busy}
                            />
                        )}
                        {page === 'suggestions' && suggestTab === 'Contradictions' && (
                            <IrlAiSuggestionsContradictions
                                {...suggestionProps}
                                items={suggestions.filter(item => item.proposalKind === 'CONTRADICTION' && isPendingSuggestion(item))}
                                onAccept={handleAccept}
                                onDismiss={handleDismiss}
                                onFollowUp={handleFollowUp}
                                busy={busy}
                            />
                        )}
                    </div>
                </div>
            </div>
            {createOpen && (
                <IrlCreateRequest
                    draft={draft}
                    onChange={next => {
                        const changedWorkstream = next.workstream !== draft.workstream;
                        setDraft(changedWorkstream ? { ...next, folder: '' } : next);
                    }}
                    workstreams={workstreams}
                    members={members}
                    folders={folders}
                    onCreate={handleCreate}
                    onCancel={() => {
                        setCreateOpen(false);
                        setInformationRequestModal?.(false);
                    }}
                />
            )}
            {cannotProvideOpen && (
                <div className={styles.overlay}>
                    <div className={styles.modal}>
                        <div className={styles.modalHead}>
                            <h2 className={styles.modalTitle}>Cannot provide</h2>
                        </div>
                        <div className={styles.modalBody}>
                            <p className={styles.modalIntro}>Choose why this request cannot be provided.</p>
                            {CANNOT_PROVIDE_REASONS.map(reason => (
                                <button
                                    key={reason.id}
                                    type="button"
                                    className={styles.scopeOption}
                                    onClick={() => handleTransition('cannot-provide', { reason: reason.id })}>
                                    {reason.label}
                                </button>
                            ))}
                        </div>
                        <div className={styles.modalFoot}>
                            <button type="button" className={styles.mutedBtn} onClick={() => setCannotProvideOpen(false)}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {aiStep === 'choose' && (
                <IrlAiReview
                    onSelect={mode => {
                        if (mode === 'initial') {
                            setAiStep(null);
                            setMenuOpen(false);
                            setPage('generate');
                            return;
                        }
                        setAiMode(mode);
                        setAiStep('scope');
                    }}
                    onCancel={() => setAiStep(null)}
                    modes={LIVE_AI_MODES}
                />
            )}
            {aiStep === 'scope' && (
                <IrlAiReviewScope
                    scopes={aiScopes}
                    value={aiScope}
                    onChange={setAiScope}
                    onNext={() => setAiStep('instructions')}
                    onBack={() => setAiStep('choose')}
                    onClose={() => setAiStep(null)}
                />
            )}
            {aiStep === 'instructions' && (
                <IrlAiReviewInstructions
                    value={aiInstructions}
                    onChange={setAiInstructions}
                    onNext={() => setAiStep('confirm')}
                    onBack={() => setAiStep('scope')}
                    onClose={() => setAiStep(null)}
                />
            )}
            {aiStep === 'confirm' && (
                <IrlAiReviewConfirm
                    mode={LIVE_AI_MODES.find(item => item.id === aiMode)?.title || 'Review responses'}
                    scope={aiScope}
                    onRun={runAiReview}
                    onCancel={() => setAiStep(null)}
                />
            )}
            {aiStep === 'progress' && <IrlAiReviewProgress onLeave={() => setAiStep(null)} />}
            {aiStep === 'complete' && (
                <IrlAiReviewComplete
                    onReview={() => {
                        setAiStep(null);
                        openWaitingSuggestions({ mode: aiMode });
                    }}
                    counts={reviewResult}
                    onClose={() => setAiStep(null)}
                />
            )}
        </ModuleShell>
        <FileViewerModal
            openModal={viewerOpen}
            setOpenModal={setViewerOpen}
            selectedFile={viewerFile}
            setSelectedFile={setViewerFile}
            folderFiles={viewerFile ? [viewerFile] : []}
            selectedFileIndex={0}
            workstream={viewerWorkstream}
        />
        </>
    );
}
